import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import './index.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient();

// ── Force Unregister Service Workers for Dev ─────────────────────────────────
if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (let registration of registrations) {
        registration.unregister();
      }
    }).catch(err => console.warn('[SW] Unregister failed:', err));
  }
} else if ('serviceWorker' in navigator) {
  // ── Register Service Worker for Production ─────────────────────────────────
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(registration => {
        console.log('[SW] Registered successfully:', registration.scope);
        
        // Check for updates every 1 hour
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);

        // ── Push Notification Registration Flow ──────────────────────────────
        // If permission is already granted, attempt automatic push subscription renewal.
        if ('Notification' in window && Notification.permission === 'granted') {
          subscribeUserToPush(registration);
        }
      })
      .catch(error => {
        console.error('[SW] Registration failed:', error);
      });
  });
}

// ── PWA Screen Orientation Lock (Locks PWA on mobile phones to portrait) ───
const lockMobilePortrait = () => {
  if (typeof window !== 'undefined' && window.screen && window.screen.orientation && typeof window.screen.orientation.lock === 'function') {
    window.screen.orientation.lock('portrait-primary').catch(() => {
      // Browser orientation lock API is active when installed in standalone mode
    });
  }
};
lockMobilePortrait();
if (typeof window !== 'undefined') {
  window.addEventListener('load', lockMobilePortrait);
  document.addEventListener('DOMContentLoaded', lockMobilePortrait);
  window.addEventListener('orientationchange', lockMobilePortrait, { passive: true });
}

// ── Push Notification Helpers (Safe Fallback) ────────────────────────────────
function subscribeUserToPush(registration) {
  if (!('PushManager' in window)) return;
  
  // Public VAPID key from environment variable
  const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!vapidKey) return; // Silent fallback if public VAPID key is not configured
  
  registration.pushManager.getSubscription()
    .then(subscription => {
      if (subscription) return subscription;
      
      const convertedKey = urlBase64ToUint8Array(vapidKey);
      return registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey
      });
    })
    .then(subscription => {
      if (!subscription) return;
      console.log('[Push] Subscription active:', subscription);
      // Fire-and-forget: notify the backend database of the active push subscription token
      fetch('/api/push-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription)
      }).catch(() => {});
    })
    .catch(err => console.warn('[Push] Subscription registration failed:', err));
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// ── Render ────────────────────────────────────────────────────────────────────
const rootElement = document.getElementById('root');
if (rootElement) {
  ReactDOM.createRoot(rootElement).render(
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  );
}

// Hide index.html loading screen once React mounts
window.addEventListener('load', () => {
  setTimeout(() => {
    const loading = document.getElementById('loading');
    if (loading) {
      loading.style.opacity = '0';
      loading.style.transition = 'opacity 0.3s';
      setTimeout(() => loading.remove(), 300);
    }
  }, 300);
});
